import type { ChecklistItem as FlatChecklistItem } from '../types/schema'
import type { TimelineElement } from '../types/timelineElement'
import type { ExploreWorkspaceSeed } from './workspaceSeeds'

/**
 * Aria & Co. — Boutique Wedding Planning Agency
 *
 * A "full demo" workspace shipped with the app. Chosen because a wedding
 * agency naturally uses every surface Timelining offers: timeline (event
 * dates), checklists (per-wedding), forms (client intake), calendar, chat,
 * reports (venue costs), design (mood boards), automate (vendor reminders),
 * stock (rental inventory), budget (per event), meetings (vendor calls),
 * RAID (risk log).
 *
 * Auto-mounts as the active workspace on first login (see WorkspaceContext).
 */

const ID = 'explore-seed-aria-and-co'
const SLUG = 'demo-aria-and-co'
const BOARD = 'DASHBOARD' as const
const CURRENCY = 'USD'
const DEMO_USER_IDS = ['aria-founder', 'kai-lead', 'jules-designer', 'sam-ops', 'noor-accounts'] as const
const READ_ACCESS = [...DEMO_USER_IDS] as string[]
const WRITE_ACCESS = ['aria-founder', 'kai-lead']

const COLORS = {
  chen: '#db2777',
  patel: '#7c3aed',
  moretti: '#0ea5e9',
  johnson: '#16a34a',
  liu: '#f59e0b',
  garcia: '#ef4444',
} as const

type ElementInput = {
  id: string
  type: TimelineElement['type']
  title: string
  description: string
  parentId: string | null
  routePath: string
  parentRoutePath?: string
  progress: number
  status: TimelineElement['boardStatus']
  priority: number
  startDate: string
  endDate: string
  budgetLimit: number
  estimatedCost: number
  color: `#${string}`
}

const el = (input: ElementInput): TimelineElement => ({
  id: input.id,
  type: input.type,
  title: input.title,
  description: input.description,
  weight: input.type === 'GOAL' ? 10 : input.type === 'TASK' ? 5 : 2,
  progress: input.progress,
  parentId: input.parentId,
  board: BOARD,
  publicId: `${SLUG.toUpperCase()}-${input.routePath.toUpperCase()}`,
  routePath: input.routePath,
  parentRoutePath: input.parentRoutePath ?? '',
  workspaceId: ID,
  startDate: input.startDate,
  endDate: input.endDate,
  boardStatus: input.status,
  workflowStatus: input.status,
  priority: input.priority,
  dependencyNodeIds: [],
  recordVersion: 1,
  statusSyncOrigin: 'SEED',
  statusSyncVersion: 1,
  readinessScore: input.progress,
  readyChecklistCount: Math.round(input.progress / 25),
  totalChecklistCount: 4,
  budgetLimit: input.budgetLimit,
  estimatedCost: input.estimatedCost,
  color: input.color,
  checklist: [],
  readAccess: READ_ACCESS,
  writeAccess: WRITE_ACCESS,
  directReadAccess: READ_ACCESS,
  directWriteAccess: WRITE_ACCESS,
  lastUpdatedBy: 'aria-founder',
})

type ChecklistInput = {
  id: string
  parentContainerId: string
  title: string
  description: string
  routePath: string
  parentRoutePath: string
  completed: boolean
  status: FlatChecklistItem['workflowStatus']
  priority: number
  startDate: string
  endDate: string
  x: number
  y: number
  budgetLimit: number
  actualSpend: number
  estimatedCost: number
  soldUnits: number
  assignee: string
}

const ck = (input: ChecklistInput): FlatChecklistItem => ({
  id: input.id,
  parentContainerId: input.parentContainerId,
  workspaceId: ID,
  workspaceSlug: SLUG,
  board: BOARD,
  publicId: `${SLUG.toUpperCase()}-${input.routePath.toUpperCase()}`,
  routePath: input.routePath,
  parentRoutePath: input.parentRoutePath,
  title: input.title,
  description: input.description,
  completionCriteria: 'Owner confirms all vendor deliverables received; sign-off from Aria; photos uploaded to shared album.',
  boardStatus: input.completed ? 'COMPLETE' : input.status === 'ONGOING' ? 'IN_PROGRESS' : 'TODO',
  workflowStatus: input.status,
  priority: input.priority,
  startDate: input.startDate,
  endDate: input.endDate,
  recordVersion: 1,
  statusSyncOrigin: 'SEED',
  statusSyncVersion: 1,
  createdById: 'aria-founder',
  createdByName: 'Aria Chen',
  assigneeId: input.assignee.toLowerCase().replace(/\s+/g, '-'),
  assigneeName: input.assignee,
  lastUpdatedBy: 'aria-founder',
  lastUpdatedByName: 'Aria Chen',
  isCompleted: input.completed,
  weightage: input.completed ? 2 : 3,
  position: { x: input.x, y: input.y },
  workspaceCanvasLayoutVersion: 1,
  prerequisites: [],
  dependencyNodeIds: [],
  blockedByExternal: null,
  requiresProofFile: !input.completed,
  requiresLocationPin: false,
  isHelperVisible: true,
  budgetLimit: input.budgetLimit,
  actualSpend: input.actualSpend,
  estimatedCost: input.estimatedCost,
  soldUnits: input.soldUnits,
  currencyCode: CURRENCY,
  complexityStars: input.priority,
  urgencyStars: input.priority,
  importanceStars: Math.min(5, input.priority + 1),
  estimatedHours: 6 + input.priority * 2,
  actualCost: input.actualSpend,
  scheduleMode: 'DATED',
  scheduleStartDate: input.startDate,
  scheduleEndDate: input.endDate,
  hasTimeWindow: true,
  scheduleTimeFrom: '09:00',
  scheduleTimeTo: '17:00',
})

// Six weddings across the season — each a GOAL with 2–3 TASKs and rich
// checklists. Dates deliberately span past, current, and near-future so the
// timeline view has motion whenever a visitor opens the demo.

const weddings = [
  { id: 'chen', couple: 'Chen × Rivera', venue: 'Golden Gate Club', date: '2026-08-22', color: COLORS.chen, progress: 82, status: 'IN_PROGRESS' as const, priority: 5, budget: 65000, spent: 41200 },
  { id: 'patel', couple: 'Patel × Sharma', venue: 'Vineyard Estate', date: '2026-09-14', color: COLORS.patel, progress: 58, status: 'IN_PROGRESS' as const, priority: 5, budget: 92000, spent: 39400 },
  { id: 'moretti', couple: 'Moretti × Kim', venue: 'Palace of Fine Arts', date: '2026-10-05', color: COLORS.moretti, progress: 34, status: 'IN_PROGRESS' as const, priority: 4, budget: 78000, spent: 21000 },
  { id: 'johnson', couple: 'Johnson × Wu', venue: 'Bay Front Loft', date: '2026-10-26', color: COLORS.johnson, progress: 22, status: 'TODO' as const, priority: 4, budget: 54000, spent: 9800 },
  { id: 'liu', couple: 'Liu × Anders', venue: 'Sonoma Barn', date: '2026-11-15', color: COLORS.liu, progress: 12, status: 'TODO' as const, priority: 3, budget: 48000, spent: 3200 },
  { id: 'garcia', couple: 'García × Nakamura', venue: 'Napa Chateau', date: '2027-02-14', color: COLORS.garcia, progress: 4, status: 'TODO' as const, priority: 3, budget: 118000, spent: 1400 },
]

const elements: TimelineElement[] = []
const checklistItems: FlatChecklistItem[] = []

for (const w of weddings) {
  const goalId = `${ID}-goal-${w.id}`
  const eventDate = new Date(w.date)
  const startDate = new Date(eventDate.getTime() - 180 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  const endDate = new Date(eventDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)

  elements.push(el({
    id: goalId,
    type: 'GOAL',
    title: `${w.couple} — ${w.venue}`,
    description: `Full wedding production for ${w.couple} at ${w.venue} on ${w.date}. Includes vendors, catering, florals, music, day-of coordination, and post-event reconciliation.`,
    parentId: null,
    routePath: w.id,
    progress: w.progress,
    status: w.status,
    priority: w.priority,
    startDate,
    endDate,
    budgetLimit: w.budget * 100,
    estimatedCost: w.spent * 100,
    color: w.color,
  }))

  // Task 1: Vendor lockdown
  const vendorTaskId = `${ID}-task-${w.id}-vendors`
  elements.push(el({
    id: vendorTaskId,
    type: 'TASK',
    title: 'Vendors and contracts',
    description: 'Book florist, caterer, DJ, photographer, videographer; execute contracts and lock deposits.',
    parentId: goalId,
    routePath: `${w.id}/vendors`,
    parentRoutePath: w.id,
    progress: Math.min(100, w.progress + 10),
    status: w.status,
    priority: w.priority,
    startDate,
    endDate: new Date(eventDate.getTime() - 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    budgetLimit: Math.round(w.budget * 0.55) * 100,
    estimatedCost: Math.round(w.spent * 0.6) * 100,
    color: w.color,
  }))

  // Task 2: Guest and logistics
  const guestTaskId = `${ID}-task-${w.id}-guests`
  elements.push(el({
    id: guestTaskId,
    type: 'TASK',
    title: 'Guest experience and logistics',
    description: 'RSVPs, seating chart, transport, accommodations block, welcome bags, timeline for the day.',
    parentId: goalId,
    routePath: `${w.id}/guests`,
    parentRoutePath: w.id,
    progress: Math.max(5, w.progress - 15),
    status: w.status === 'IN_PROGRESS' ? 'IN_PROGRESS' : 'TODO',
    priority: w.priority,
    startDate: new Date(eventDate.getTime() - 120 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    endDate: new Date(eventDate.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    budgetLimit: Math.round(w.budget * 0.25) * 100,
    estimatedCost: Math.round(w.spent * 0.25) * 100,
    color: w.color,
  }))

  // Task 3: Day-of and reconciliation
  const dayOfTaskId = `${ID}-task-${w.id}-dayof`
  elements.push(el({
    id: dayOfTaskId,
    type: 'TASK',
    title: 'Day-of run and post-event',
    description: 'Ceremony rehearsal, day-of coordination, vendor payments reconciliation, thank-you cards, portfolio photos.',
    parentId: goalId,
    routePath: `${w.id}/dayof`,
    parentRoutePath: w.id,
    progress: Math.max(0, w.progress - 30),
    status: 'TODO',
    priority: w.priority,
    startDate: new Date(eventDate.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    endDate,
    budgetLimit: Math.round(w.budget * 0.20) * 100,
    estimatedCost: Math.round(w.spent * 0.15) * 100,
    color: w.color,
  }))

  // Checklists — 4 vendor items + 3 guest items = 7 per wedding × 6 = 42
  const vendorChecks: Array<[string, boolean, ChecklistInput['status'], string, number, number]> = [
    ['Book photographer + videographer', w.progress > 40, w.progress > 40 ? 'COMPLETED' : 'ONGOING', 'Kai Ramirez', 6500, 6500],
    ['Confirm catering menu tasting', w.progress > 55, w.progress > 55 ? 'COMPLETED' : 'ASSIGNED', 'Sam Osei', 3200, 2400],
    ['Lock florist arrangement design', w.progress > 45, w.progress > 45 ? 'COMPLETED' : 'ONGOING', 'Jules Park', 4800, 4200],
    ['Sign DJ and MC contracts', w.progress > 65, w.progress > 65 ? 'COMPLETED' : 'CREATED', 'Kai Ramirez', 2900, 2900],
  ]
  vendorChecks.forEach((ck_, idx) => {
    const [title, done, status, assignee, limit, spend] = ck_
    checklistItems.push(ck({
      id: `${ID}-check-${w.id}-v${idx}`,
      parentContainerId: vendorTaskId,
      title: `${title}`,
      description: `Recurring vendor checkpoint for ${w.couple}. Owner uploads signed contract as proof; Aria signs off.`,
      routePath: `${w.id}/vendors/c${idx}`,
      parentRoutePath: `${w.id}/vendors`,
      completed: done,
      status,
      priority: w.priority,
      startDate,
      endDate: new Date(eventDate.getTime() - (90 - idx * 20) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      x: 80 + idx * 220,
      y: 80,
      budgetLimit: limit * 100,
      actualSpend: (done ? spend : Math.round(spend * 0.4)) * 100,
      estimatedCost: spend * 100,
      soldUnits: 0,
      assignee,
    }))
  })

  const guestChecks: Array<[string, boolean, ChecklistInput['status'], string]> = [
    ['Send save-the-dates', w.progress > 20, w.progress > 20 ? 'COMPLETED' : 'ASSIGNED', 'Noor Habib'],
    ['Finalize seating chart', w.progress > 70, w.progress > 70 ? 'COMPLETED' : 'CREATED', 'Sam Osei'],
    ['Confirm hotel block', w.progress > 35, w.progress > 35 ? 'COMPLETED' : 'ASSIGNED', 'Noor Habib'],
  ]
  guestChecks.forEach((ck_, idx) => {
    const [title, done, status, assignee] = ck_
    checklistItems.push(ck({
      id: `${ID}-check-${w.id}-g${idx}`,
      parentContainerId: guestTaskId,
      title,
      description: `Guest-track work for ${w.couple}. Owner attaches guest list CSV as proof once complete.`,
      routePath: `${w.id}/guests/c${idx}`,
      parentRoutePath: `${w.id}/guests`,
      completed: done,
      status,
      priority: Math.max(2, w.priority - 1),
      startDate,
      endDate: new Date(eventDate.getTime() - (60 - idx * 15) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      x: 80 + idx * 220,
      y: 260,
      budgetLimit: 60000,
      actualSpend: done ? 42000 : 12000,
      estimatedCost: 55000,
      soldUnits: 120,
      assignee,
    }))
  })
}

// Workspace-scoped checklists (the standalone Checklists route) — these
// hang off the special WORKSPACE_DEFAULT_CHECKLIST_ID rather than a task, so
// they populate the /checklists canvas when a viewer opens it directly.
const WORKSPACE_DEFAULT_CHECKLIST_ID = 'workspace-default-checklist'

const workspaceChecklistTitles: Array<[string, boolean, ChecklistInput['status'], string, number]> = [
  ['Weekly team standup prep', true, 'COMPLETED', 'Aria Chen', 8],
  ['Monthly vendor pricing review', false, 'ONGOING', 'Kai Ramirez', 45],
  ['Insurance renewal — Q4 policy', false, 'ASSIGNED', 'Noor Habib', 60],
  ['Rebuild ceremony arch inventory list', true, 'COMPLETED', 'Sam Osei', -5],
  ['Photographer contract template refresh', false, 'CREATED', 'Jules Park', 90],
  ['Client testimonial capture rota', false, 'ONGOING', 'Aria Chen', 30],
]
workspaceChecklistTitles.forEach(([title, done, status, assignee, endDaysFromNow], idx) => {
  const endDate = new Date(Date.now() + endDaysFromNow * 86_400_000).toISOString().slice(0, 10)
  const startDate = new Date(Date.now() - 14 * 86_400_000).toISOString().slice(0, 10)
  checklistItems.push(ck({
    id: `${ID}-check-workspace-${idx}`,
    parentContainerId: WORKSPACE_DEFAULT_CHECKLIST_ID,
    title,
    description: 'Workspace-scoped checklist item — not tied to a specific wedding. Lives in the standalone Checklists canvas.',
    routePath: `workspace/c${idx}`,
    parentRoutePath: 'workspace',
    completed: done,
    status,
    priority: 3,
    startDate,
    endDate,
    x: 80 + (idx % 3) * 260,
    y: 80 + Math.floor(idx / 3) * 200,
    budgetLimit: 30_000,
    actualSpend: done ? 24_000 : 6_000,
    estimatedCost: 27_000,
    soldUnits: 0,
    assignee,
  }))
})

export const ARIA_AND_CO_SEED: ExploreWorkspaceSeed = {
  id: ID,
  name: 'Aria & Co. — Boutique Wedding Planning',
  slug: SLUG,
  summary: 'A twelve-person planning agency running six concurrent weddings — full board, budgets, vendor lockdown, guest ops, and day-of coordination. The complete Timelining product on display.',
  tags: ['demo', 'featured', 'wedding', 'agency', 'full-tour'],
  metrics: [
    { label: 'Active weddings', value: '6' },
    { label: 'Team', value: '12 planners' },
    { label: 'Budget under management', value: '$455k' },
    { label: 'Next event', value: 'Aug 22' },
  ],
  currency: CURRENCY,
  budgetLabel: '$116k / $455k',
  quotaLabel: '6 / 8 events per season',
  sensitiveDemo: {
    label: 'Sample sensitive fields (demo)',
    items: [
      'aria@example.test',
      'Vendor invoice AC-2026-0842',
      'Bank wire reference DEMO-WIRE-4402',
      'Client contract e-sign: DEMO-DS-9910',
    ],
  },
  members: [
    { name: 'Aria Chen', email: 'aria@example.test', role: 'ADMIN' },
    { name: 'Kai Ramirez', email: 'kai@example.test', role: 'EDITOR' },
    { name: 'Jules Park', email: 'jules@example.test', role: 'EDITOR' },
    { name: 'Sam Osei', email: 'sam@example.test', role: 'EDITOR' },
    { name: 'Noor Habib', email: 'noor@example.test', role: 'EDITOR' },
    { name: 'Devi Sharma', email: 'devi@example.test', role: 'VIEWER' },
    { name: 'Marcus Lee', email: 'marcus@example.test', role: 'VIEWER' },
    { name: 'Petra Okafor', email: 'petra@example.test', role: 'VIEWER' },
    { name: 'Yuki Tanaka', email: 'yuki@example.test', role: 'HELPER' },
    { name: 'Rafael Diaz', email: 'rafael@example.test', role: 'HELPER' },
    { name: 'Emma Larsen', email: 'emma@example.test', role: 'HELPER' },
    { name: 'Aya Fadel', email: 'aya@example.test', role: 'HELPER' },
  ],
  elements,
  checklistItems,
  reports: [{
    id: 'aria-and-co-weekly-delivery-brief',
    workspaceId: ID,
    kind: 'WIZARD',
    status: 'READY',
    createdBy: 'aria-founder',
    createdAtMs: Date.UTC(2026, 7, 14, 9),
    updatedAtMs: Date.UTC(2026, 7, 14, 9),
    templateId: 'weekly-executive',
    theme: 'AURORA',
    sources: [],
    document: {
      meta: { title: 'Weekly Wedding Delivery Brief', subtitle: 'Six active events, vendor readiness, and budget watch', author: 'Aria Chen', coverArt: 'AURORA', theme: 'AURORA', audience: 'EXECUTIVE' },
      blocks: [
        { type: 'cover' },
        { type: 'heading', id: 'delivery-status', level: 2, text: 'Delivery status' },
        { type: 'paragraph', markdown: 'Five of six weddings remain on plan. The final vendor confirmations for the Rivera and Nakamura events are the primary watch items.' },
      ],
    },
  }],
  featured: true,
  heroGradient: 'SUNSET',
  verified: true,
}
